import { get } from 'lodash';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { map, mergeMap, reduce } from 'rxjs/operators';

import {
  DataConfiguration,
  DataConfigurationType,
  IArrayDataConfiguration,
  IObjectDataConfiguration,
  IPrimitiveDataConfiguration,
  nonPrimitiveTypes,
} from './types/data-configuration.types';

interface IAbstractControl {
  value: any;
}
type IFormControl = IAbstractControl;
type IFormArray = IAbstractControl;
type IFormGroup = IAbstractControl;

export class DynamicFormBuilderService {
  constructor(
    private readonly observableCreator: typeof from,
    private readonly formBuilder: any
  ) {}

  build(dataConfig: DataConfiguration, data?: any): Observable<IAbstractControl | never> {
    switch (dataConfig.type) {
      case DataConfigurationType.Array: {
        return this.buildArray(dataConfig, data);
      }
      case DataConfigurationType.Object: {
        return this.buildObject(dataConfig, data);
      }
      case DataConfigurationType.Boolean:
      case DataConfigurationType.Date:
      case DataConfigurationType.Number:
      case DataConfigurationType.String: {
        return this.buildPrimitive(dataConfig, data);
      }
      default: {
        return throwError('Unknown DataConfiguration type');
      }
    }
  }

  private buildArray(
    dataConfig: IArrayDataConfiguration,
    data?: any[]
  ): Observable<IFormArray> | Observable<IFormControl> {
    const { items } = dataConfig;

    if (!nonPrimitiveTypes.includes(dataConfig.items.type)) {
      return this.buildPrimitive(dataConfig.items as IPrimitiveDataConfiguration, data);
    }

    return this.observableCreator(data || []).pipe(
      mergeMap((dataEntry) => this.build(items, dataEntry)),
      reduce((acc: IAbstractControl[], control: IAbstractControl) => [...acc, control], []),
      map((controls) => this.formBuilder.array(controls))
    );
  }

  private buildObject(dataConfig: IObjectDataConfiguration, data?: any): Observable<IFormGroup> {
    const { properties } = dataConfig;

    return this.observableCreator(Object.keys(properties)).pipe(
      mergeMap((key) => forkJoin([of(key), this.build(properties[key], get(data, key))])),
      reduce((acc: Record<string, IAbstractControl>, [key, value]) => ({ ...acc, [key]: value }), {}),
      map((config) => this.formBuilder.group(config))
    );
  }

  private buildPrimitive(_dataConfig: IPrimitiveDataConfiguration, data?: any): Observable<IFormControl> {
    return of(this.formBuilder.control(data));
  }
}
